import React, { useState, useEffect } from 'react';
import { Navigation, Plus, Trash2, Power, PowerOff, BarChart } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { watchlistService } from '../../services/watchlistService';
import { colors } from '../../config/colors';
import { Button } from '../UI/button';
import { ModalDialog } from '../UI/modal-dialog';
import toast from 'react-hot-toast';
import RouteAnalysisCard from './RouteAnalysisCard';
import {
  collectEmbeddedRouteAnalyses,
  routesNeedingAnalysis,
} from '../../utils/watchlistReadModelAdapter';

const RouteManagementModal = ({ companyId, onClose, onRouteAdded, watchlistItems = [] }) => {
  const { t } = useLocale();
  const [routes, setRoutes] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [routeAnalyses, setRouteAnalyses] = useState({});
  const [analyzingRoutes, setAnalyzingRoutes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    startPointAddressId: '',
    endPointAddressId: '',
    endPointType: 'same',
    endPointCompanyId: '',
    routeName: '',
    description: '',
    isActive: true
  });
  const [otherCompanyAddresses, setOtherCompanyAddresses] = useState([]);
  const [loadingOtherAddresses, setLoadingOtherAddresses] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const otherCompanies = (watchlistItems || []).filter(
    (item) => (item.companyId ?? item.company_id) !== companyId
  );
  const canSelectOtherCompany = otherCompanies.length > 0;

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user')).uid;
      
      // Fetch addresses and routes in parallel
      const [addressesResult, routesResult] = await Promise.all([
        watchlistService.getUserAddresses(userId, companyId),
        watchlistService.getRoutes(userId, companyId)
      ]);
      
      if (addressesResult.success) {
        setAddresses(addressesResult.data || []);
      } else {
        toast.error(addressesResult.message || 'Failed to fetch addresses');
      }

      if (routesResult.success) {
        const fetchedRoutes = routesResult.data || [];
        setRoutes(fetchedRoutes);

        const watchlistItem = (watchlistItems || []).find(
          (item) => (item.companyId ?? item.company_id) === companyId
        );
        if (watchlistItem) {
          const embedded = collectEmbeddedRouteAnalyses([watchlistItem]);
          setRouteAnalyses(embedded);
          const missing = routesNeedingAnalysis([watchlistItem]);
          if (missing.length > 0) {
            fetchRouteAnalyses(userId, missing, embedded);
          }
        } else if (fetchedRoutes.length > 0) {
          fetchRouteAnalyses(
            userId,
            fetchedRoutes.map((route) => ({
              routeId: route.routeId ?? route.route_id,
              _ownerCompanyId: companyId,
            }))
          );
        }
      } else {
        toast.error(routesResult.message || 'Failed to fetch routes');
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRouteAnalyses = async (userId, routesToAnalyze, seedAnalyses = {}) => {
    const result = await watchlistService.getRouteAnalysesBatch(userId, routesToAnalyze);
    const analysesMap = { ...seedAnalyses };
    if (!result.success || !Array.isArray(result.data)) {
      setRouteAnalyses(analysesMap);
      return;
    }
    result.data.forEach((analysis) => {
      const routeId = analysis.routeId ?? analysis.route_id;
      if (routeId != null) {
        analysesMap[routeId] = analysis;
      }
    });

    setRouteAnalyses(analysesMap);
  };

  const handleAnalyzeRoute = async (routeId) => {
    try {
      setAnalyzingRoutes(prev => ({ ...prev, [routeId]: true }));
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user')).uid;
      
      const result = await watchlistService.getRouteAnalysis(userId, companyId, routeId);
      
      if (result.success && result.data) {
        setRouteAnalyses(prev => ({ ...prev, [routeId]: result.data }));
        toast.success('Route analysis updated successfully');
      } else {
        toast.error(result.message || 'Failed to analyze route');
      }
    } catch (error) {
      toast.error('Failed to analyze route');
    } finally {
      setAnalyzingRoutes(prev => ({ ...prev, [routeId]: false }));
    }
  };

  useEffect(() => {
    if (formData.endPointType !== 'other' || !formData.endPointCompanyId) {
      setOtherCompanyAddresses([]);
      return;
    }
    let cancelled = false;
    setLoadingOtherAddresses(true);
    const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user') || '{}').uid;
    if (!userId) {
      setLoadingOtherAddresses(false);
      return;
    }
    watchlistService.getUserAddresses(userId, formData.endPointCompanyId).then((res) => {
      if (!cancelled && res.success && Array.isArray(res.data)) {
        setOtherCompanyAddresses(res.data);
        setFormData((prev) => ({ ...prev, endPointAddressId: '' }));
      }
    }).finally(() => {
      if (!cancelled) setLoadingOtherAddresses(false);
    });
    return () => { cancelled = true; };
  }, [formData.endPointType, formData.endPointCompanyId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'endPointType' ? { endPointCompanyId: '', endPointAddressId: '' } : {}),
      ...(name === 'endPointCompanyId' ? { endPointAddressId: '' } : {})
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.startPointAddressId) {
      newErrors.startPointAddressId = t('routeModalStartPointRequired');
    }

    if (!formData.endPointAddressId) {
      newErrors.endPointAddressId = t('routeModalEndPointRequired');
    }

    const isSameCompany = formData.endPointType === 'same';
    if (isSameCompany && formData.startPointAddressId === formData.endPointAddressId) {
      newErrors.endPointAddressId = t('routeModalStartEndDifferent');
    }

    if (!formData.routeName.trim()) {
      newErrors.routeName = t('routeModalRouteNameRequired');
    } else if (formData.routeName.length > 100) {
      newErrors.routeName = t('routeModalRouteNameMax');
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = t('routeModalDescriptionMax');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user')).uid;
      const payload = {
        startPointAddressId: formData.startPointAddressId,
        endPointAddressId: formData.endPointAddressId,
        routeName: formData.routeName,
        description: formData.description,
        isActive: formData.isActive
      };
      if (formData.endPointType === 'other' && formData.endPointCompanyId) {
        payload.endPointCompanyId = formData.endPointCompanyId;
      }
      const result = await watchlistService.addRoute(userId, companyId, payload);

      if (result.success) {
        toast.success(t('routeAddedSuccess'));
        setFormData({
          startPointAddressId: '',
          endPointAddressId: '',
          endPointType: 'same',
          endPointCompanyId: '',
          routeName: '',
          description: '',
          isActive: true
        });
        setShowAddForm(false);
        fetchData(); // Refresh list
        if (onRouteAdded) {
          onRouteAdded(result.data);
        }
      } else {
        toast.error(result.message || 'Failed to add route');
      }
    } catch (error) {
      toast.error('Failed to add route');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleRouteStatus = async (routeId, currentStatus) => {
    try {
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user')).uid;
      const result = await watchlistService.updateRouteStatus(userId, companyId, routeId, !currentStatus);

      if (result.success) {
        toast.success(`Route ${!currentStatus ? 'activated' : 'deactivated'}`);
        fetchData(); // Refresh list
      } else {
        toast.error(result.message || 'Failed to update route status');
      }
    } catch (error) {
      toast.error('Failed to update route status');
    }
  };

  const handleDelete = async (routeId) => {
    if (!window.confirm('Are you sure you want to delete this route?')) {
      return;
    }

    try {
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user')).uid;
      const result = await watchlistService.removeRoute(userId, companyId, routeId);

      if (result.success) {
        toast.success('Route deleted successfully');
        fetchData(); // Refresh list
      } else {
        toast.error(result.message || 'Failed to delete route');
      }
    } catch (error) {
      toast.error('Failed to delete route');
    }
  };

  const getAddressLabel = (addressId, fromAddresses = addresses) => {
    const address = fromAddresses.find(a => (a.addressId ?? a.address_id) === addressId);
    if (!address) return null;
    return `${address.city || ''}, ${address.country || ''}`.trim();
  };

  const getAddressFullLabel = (route) => {
    const startAddress = addresses.find(a => (a.addressId ?? a.address_id) === (route.startPointAddressId ?? route.start_point_address_id));
    const endCompanyId = route.endPointCompanyId ?? route.end_point_company_id;
    const endAddressInThisCompany = addresses.find(a => (a.addressId ?? a.address_id) === (route.endPointAddressId ?? route.end_point_address_id));
    const endAddressFromOther = endCompanyId && otherCompanyAddresses.length > 0
      ? otherCompanyAddresses.find(a => (a.addressId ?? a.address_id) === (route.endPointAddressId ?? route.end_point_address_id))
      : null;
    const endAddress = endAddressInThisCompany || endAddressFromOther;
    const startStr = startAddress
      ? (startAddress.getShortAddress ? startAddress.getShortAddress() : `${startAddress.streetAddress || ''}, ${startAddress.city || ''}, ${startAddress.country || ''}`.trim())
      : t('unknownAddress');
    const endStr = endAddress
      ? (endAddress.getShortAddress ? endAddress.getShortAddress() : `${endAddress.streetAddress || ''}, ${endAddress.city || ''}, ${endAddress.country || ''}`.trim())
      : (endCompanyId ? `${endCompanyId} (${t('routeModalOtherCompany')})` : t('unknownAddress'));
    return { startStr, endStr };
  };

  return (
    <ModalDialog
      open
      onClose={onClose}
      title={t('routeManageTitle')}
      description={t('routeManageDescription', { companyId })}
      className="max-w-4xl"
      closeLabel={t('dialogCloseLabel')}
    >
            {/* Info Message */}
            {addresses.length < 2 && (
              <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: colors.warning[50], borderLeft: `4px solid ${colors.warning[500]}` }}>
                <p className="text-sm font-medium" style={{ color: colors.warning[700] }}>
                  {t('routeManageNeedTwoAddresses')}
                </p>
              </div>
            )}

            {/* Add Route Button */}
            {!showAddForm && addresses.length >= 2 && (
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full mb-6 py-4 px-6 rounded-xl border-2 border-dashed hover:border-solid transition-all flex items-center justify-center space-x-2"
                style={{
                  borderColor: colors.accent[300],
                  color: colors.accent[600],
                  backgroundColor: colors.accent[50]
                }}
              >
                <Plus className="h-5 w-5" />
                <span className="font-medium">Add New Route</span>
              </button>
            )}

            {/* Add Route Form */}
            {showAddForm && (
              <form onSubmit={handleSubmit} className="mb-6 p-6 rounded-xl border-2" style={{ borderColor: colors.accent[200], backgroundColor: colors.accent[50] }}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text.primary }}>
                  New Route
                </h3>
                
                <div className="space-y-4">
                  {/* Route Name */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                      Route Name *
                    </label>
                    <input
                      type="text"
                      name="routeName"
                      value={formData.routeName}
                      onChange={handleChange}
                      maxLength={100}
                      placeholder={t('routeNamePlaceholder')}
                      className={`input ${errors.routeName ? 'border-red-500' : ''}`}
                    />
                    {errors.routeName && (
                      <p className="mt-1 text-sm text-red-600">{errors.routeName}</p>
                    )}
                  </div>

                  {/* Start Point (always this company) */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                      {t('routeModalStartPoint')} *
                    </label>
                    <select
                      name="startPointAddressId"
                      value={formData.startPointAddressId}
                      onChange={handleChange}
                      className={`input w-full rounded-lg border px-3 py-2 ${errors.startPointAddressId ? 'border-red-500' : ''}`}
                      style={{ borderColor: errors.startPointAddressId ? colors.danger[500] : colors.border.light }}
                    >
                      <option value="">{t('routeModalSelectAddress')}</option>
                      {addresses.map((address) => (
                        <option key={address.addressId} value={address.addressId}>
                          {getAddressLabel(address.addressId)}
                        </option>
                      ))}
                    </select>
                    {errors.startPointAddressId && (
                      <p className="mt-1 text-sm" style={{ color: colors.danger[500] }}>{errors.startPointAddressId}</p>
                    )}
                  </div>

                  {/* End: Same company vs Another company */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                      {t('routeModalEndPoint')} *
                    </label>
                    {canSelectOtherCompany && (
                      <div className="flex gap-4 mb-3">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="endPointType"
                            value="same"
                            checked={formData.endPointType === 'same'}
                            onChange={handleChange}
                            style={{ accentColor: colors.primary[500] }}
                          />
                          <span className="text-sm" style={{ color: colors.text.primary }}>{t('routeModalEndSameCompany')}</span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="endPointType"
                            value="other"
                            checked={formData.endPointType === 'other'}
                            onChange={handleChange}
                            style={{ accentColor: colors.primary[500] }}
                          />
                          <span className="text-sm" style={{ color: colors.text.primary }}>{t('routeModalEndOtherCompany')}</span>
                        </label>
                      </div>
                    )}
                    {formData.endPointType === 'same' ? (
                      <select
                        name="endPointAddressId"
                        value={formData.endPointAddressId}
                        onChange={handleChange}
                        className="input w-full rounded-lg border px-3 py-2"
                        style={{ borderColor: errors.endPointAddressId ? colors.danger[500] : colors.border.light }}
                      >
                        <option value="">{t('routeModalSelectAddress')}</option>
                        {addresses.map((address) => (
                          <option key={address.addressId} value={address.addressId}>
                            {getAddressLabel(address.addressId)}
                          </option>
                        ))}
                      </select>
                    ) : canSelectOtherCompany ? (
                      <div className="space-y-3">
                        <select
                          name="endPointCompanyId"
                          value={formData.endPointCompanyId}
                          onChange={handleChange}
                          className="input w-full rounded-lg border px-3 py-2"
                          style={{ borderColor: colors.border.light }}
                        >
                          <option value="">{t('routeModalSelectCompany')}</option>
                          {otherCompanies.map((item) => {
                            const id = item.companyId ?? item.company_id;
                            return (
                              <option key={id} value={id}>{id}</option>
                            );
                          })}
                        </select>
                        <select
                          name="endPointAddressId"
                          value={formData.endPointAddressId}
                          onChange={handleChange}
                          disabled={loadingOtherAddresses || !formData.endPointCompanyId}
                          className="input w-full rounded-lg border px-3 py-2"
                          style={{ borderColor: errors.endPointAddressId ? colors.danger[500] : colors.border.light }}
                        >
                          <option value="">{loadingOtherAddresses ? t('loading') : t('routeModalSelectAddress')}</option>
                          {otherCompanyAddresses.map((address) => (
                            <option key={address.addressId} value={address.addressId}>
                              {(address.getShortAddress && address.getShortAddress()) || `${address.city || ''}, ${address.country || ''}`.trim() || address.addressId}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    {errors.endPointAddressId && (
                      <p className="mt-1 text-sm" style={{ color: colors.danger[500] }}>{errors.endPointAddressId}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      maxLength={500}
                      rows={3}
                      placeholder={t('routeDescriptionPlaceholder')}
                      className={`input ${errors.description ? 'border-red-500' : ''}`}
                    />
                    <div className="flex items-center justify-between mt-1">
                      {errors.description && (
                        <p className="text-sm text-red-600">{errors.description}</p>
                      )}
                      <p className="text-xs text-gray-400 ml-auto">
                        {formData.description.length}/500
                      </p>
                    </div>
                  </div>

                  {/* Active Status */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="rounded"
                      style={{ accentColor: colors.accent[500] }}
                    />
                    <label htmlFor="isActive" className="text-sm font-medium" style={{ color: colors.text.secondary }}>
                      Route is active
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowAddForm(false)}
                    disabled={submitting}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        {t('routeManageAddRoute')}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Route List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : routes.length === 0 ? (
              <div className="text-center py-12">
                <Navigation className="h-12 w-12 mx-auto mb-4" style={{ color: colors.gray[400] }} />
                <h3 className="text-lg font-medium mb-2" style={{ color: colors.text.primary }}>
                  No Routes Yet
                </h3>
                <p style={{ color: colors.text.secondary }}>
                  {addresses.length < 2 
                    ? 'Add at least 2 addresses to create routes'
                    : 'Create routes to track your supply chain connections'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Analysis status banner */}
                {routes.length > 0 && (
                  <div className="p-3 rounded-lg" style={{ 
                    backgroundColor: Object.keys(routeAnalyses).length > 0 ? colors.success[50] : colors.info[50],
                    border: `1px solid ${Object.keys(routeAnalyses).length > 0 ? colors.success[200] : colors.info[200]}`
                  }}>
                    <p className="text-sm font-medium" style={{ 
                      color: Object.keys(routeAnalyses).length > 0 ? colors.success[700] : colors.info[700]
                    }}>
                      {Object.keys(routeAnalyses).length} of {routes.length} routes analyzed
                    </p>
                  </div>
                )}

                {routes.map((route) => {
                  const analysis = routeAnalyses[route.routeId];
                  
                  // If we have valid analysis data, use RouteAnalysisCard
                  if (analysis && analysis.distanceKm) {
                    return (
                      <div key={route.routeId} className="relative">
                        <RouteAnalysisCard
                          routeAnalysis={analysis}
                          onAnalyze={handleAnalyzeRoute}
                          analyzing={analyzingRoutes[route.routeId]}
                        />
                        {/* Action buttons overlay */}
                        <div className="absolute top-5 right-5 flex items-center space-x-2 z-10">
                          <button
                            onClick={() => toggleRouteStatus(route.routeId, route.isActive)}
                            className="p-2 rounded-lg transition-colors shadow-md"
                            style={{
                              color: route.isActive ? colors.warning[600] : colors.success[600],
                              backgroundColor: route.isActive ? colors.warning[50] : colors.success[50],
                              border: `1px solid ${route.isActive ? colors.warning[200] : colors.success[200]}`
                            }}
                            title={route.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {route.isActive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => handleDelete(route.routeId)}
                            className="p-2 rounded-lg transition-colors shadow-md"
                            style={{
                              color: colors.danger[600],
                              backgroundColor: colors.danger[50],
                              border: `1px solid ${colors.danger[200]}`
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  }
                  
                  // Show basic card with "Analyze Route" button for unanalyzed routes
                  return (
                    <div
                      key={route.routeId}
                      className="p-5 rounded-xl border hover:shadow-md transition-all"
                      style={{ borderColor: route.isActive ? colors.accent[200] : colors.gray[200] }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-lg font-semibold" style={{ color: colors.text.primary }}>
                              {route.routeName}
                            </h4>
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                              style={{
                                backgroundColor: route.isActive ? colors.success[100] : colors.gray[100],
                                color: route.isActive ? colors.success[700] : colors.gray[600]
                              }}
                            >
                              {route.isActive ? (
                                <>
                                  <Power className="h-3 w-3 mr-1" />
                                  Active
                                </>
                              ) : (
                                <>
                                  <PowerOff className="h-3 w-3 mr-1" />
                                  Inactive
                                </>
                              )}
                            </span>
                          </div>
                          
                          {/* Route Path */}
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="px-3 py-1 rounded-lg text-sm font-medium" style={{ backgroundColor: colors.info[100], color: colors.info[700] }}>
                              {(getAddressFullLabel(route).startStr)}
                            </span>
                            <Navigation className="h-4 w-4" style={{ color: colors.accent[500] }} />
                            <span className="px-3 py-1 rounded-lg text-sm font-medium" style={{ backgroundColor: colors.secondary[100], color: colors.secondary[700] }}>
                              {(getAddressFullLabel(route).endStr)}
                            </span>
                          </div>

                          {route.description && (
                            <p className="text-sm mt-2" style={{ color: colors.text.secondary }}>
                              {route.description}
                            </p>
                          )}
                          
                          {route.createdDate && (
                            <p className="text-xs mt-2" style={{ color: colors.text.tertiary }}>
                              Created: {new Date(route.createdDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => handleAnalyzeRoute(route.routeId)}
                            disabled={analyzingRoutes[route.routeId]}
                            className="px-3 py-2 rounded-lg transition-colors flex items-center space-x-1"
                            style={{
                              color: analyzingRoutes[route.routeId] ? colors.gray[500] : colors.primary[700],
                              backgroundColor: analyzingRoutes[route.routeId] ? colors.gray[100] : colors.primary[50],
                              border: `1px solid ${analyzingRoutes[route.routeId] ? colors.gray[300] : colors.primary[200]}`
                            }}
                          >
                            <BarChart className={`h-4 w-4 ${analyzingRoutes[route.routeId] ? 'animate-pulse' : ''}`} />
                            <span className="text-xs font-medium">
                              {analyzingRoutes[route.routeId] ? 'Analyzing...' : 'Analyze Route'}
                            </span>
                          </button>
                          <button
                            onClick={() => toggleRouteStatus(route.routeId, route.isActive)}
                            className="p-2 rounded-lg transition-colors"
                            style={{
                              color: route.isActive ? colors.warning[600] : colors.success[600],
                              backgroundColor: route.isActive ? colors.warning[50] : colors.success[50]
                            }}
                            title={route.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {route.isActive ? <PowerOff className="h-5 w-5" /> : <Power className="h-5 w-5" />}
                          </button>
                          <button
                            onClick={() => handleDelete(route.routeId)}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
    </ModalDialog>
  );
};

export default RouteManagementModal;












